import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Form } from '@unform/web';
import { useSelector, useDispatch } from 'react-redux';
import { format, parseISO, addDays, subDays } from 'date-fns';
import pt from 'date-fns/locale/pt';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

// icons
import {
  FiSearch,
  FiRefreshCw,
  FiMessageSquare,
  FiDownload,
  FiArrowLeft,
} from 'react-icons/fi';

// servicos
import api from 'services/api';

// actions
// import { show } from 'store/modules/dialog/actions';
import { show } from 'store/modules/dialog/actions';
import { updateCurrentDate } from 'store/modules/usuario/actions';
import NoSearch2 from 'assets/nosearch2.svg';
import { downloadFile } from 'utils/index';

// componentes
import Button from 'components/Button';
import Card from 'components/Card';
import Input from 'components/Input';
import Flag from 'components/Flag';
import SelectDate from 'components/SelectDate';
import NotFound from 'components/NotFound';
import Avatar from 'components/Avatar';

// estilos
import { Container } from './styles';
import { Scroll, Table, Align, Td } from 'styles/global';
import { Chamada, ChamadaEmissora } from 'pages/@types';
import Loader from 'components/ContentLoader/GenericLoader';
import { Role } from 'utils/variables';
import AppError from 'utils/AppError';
import Tooltip from 'components/Tooltip';

interface ChamadaComStatusGeral extends Chamada {
  statusgeral: {
    nome:
      | 'PENDENTE'
      | 'FILA ENVIO EXIBIDOR'
      | 'GRAVADO'
      | 'ERRO'
      | 'ENVIANDO EXIBIDOR'
      | 'NA';
    type:
      | 'pendente'
      | 'fila_envio'
      | 'gravado'
      | 'erro'
      | 'enviando_exibidor'
      | 'NA';
  };
}

export default function Chamadas() {
  /*
   * REDUX
   */
  const dispatch = useDispatch();
  const { currentDate, emissora, profile } = useSelector(
    (state: any) => state.usuario,
  );
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  /**
   * VARIAVEIS
   */
  const [loading, setLoading] = useState(false);
  const [chamadas, setChamadas] = useState<ChamadaComStatusGeral[]>([]);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [selected, setSelected] = useState<ChamadaComStatusGeral>();

  const chamadasFiltradas = chamadas.filter(ch =>
    ch.retranca.includes(filtro.toUpperCase()),
  );

  /**
   * USE MEMO
   */
  const dateFormatted = useMemo(() => {
    if (currentDate) {
      return format(new Date(currentDate), "d 'de' MMMM", { locale: pt });
    }
  }, [currentDate]);
  const navigate = useNavigate();
  /**
   * USE CALLBACK
   */
  const handleDownload = useCallback(async (url, filename) => {
    setLoadingDownload(true);
    try {
      await downloadFile({
        url,
        name: filename,
        fromAPI: true,
      });
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        const appError = err as AppError;
        console.log(appError);
        toast.error('Falha ao baixar o arquivo. Tente novamente mais tarde');
      }
    } finally {
      setLoadingDownload(false);
    }
  }, []);

  const handlePrevDay = useCallback(() => {
    dispatch(updateCurrentDate(subDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleNextDay = useCallback(() => {
    dispatch(updateCurrentDate(addDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleRessalva = useCallback(
    obseng => {
      const contentHTML = `<p>Observação: ${obseng}</p>`;
      const options = {
        type: 'info',
        contentHTML,
        confirmButtonText: 'FECHAR',
        showCancelButton: false,
        showConfirmButton: true,
      };
      dispatch(show(options));
    },
    [dispatch],
  );
  const handleDateChange = useCallback(
    date => {
      date && dispatch(updateCurrentDate(date));
    },
    [dispatch],
  );

  const handleReenviar = useCallback(
    ch => {
      setSelected(ch);
      const id = ch.chamadaemissora.find(
        (ce: ChamadaEmissora) => ce.emissora.id === emissora.value,
      ).id;
      const options = {
        type: 'question',

        confirmButtonText: 'CONFIRMAR',
        haveCallback: true,
        callback: {
          url: `/chamadasemissora/${id}`,
          method: api.patch,
          data: { status: 'pendente' },
          action: 'reenvio',
        },
        successMessage: 'Status alterado!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
        contentHTML: `<p>Iniciar um novo envio do material <b>${ch.retranca}</b> para o exibidor?</p>`,
      };
      dispatch(show(options));
    },
    [dispatch, emissora],
  );

  /**
   * USEEFFECT
   */
  useEffect(() => {
    if (callbackDialog && callbackDialog.success && selected) {
      setChamadas(prevState =>
        prevState.map(ch =>
          ch.id === selected.id
            ? {
                ...ch,
                statusgeral: {
                  nome: 'PENDENTE',
                  type: 'pendente',
                },
              }
            : ch,
        ),
      );
    }
  }, [callbackDialog, selected]);

  useEffect(() => {
    const controller = new AbortController();
    async function loadChamadas() {
      setLoading(true);
      try {
        const response = await api.get('/chamadasts/aprovadas', {
          signal: controller.signal,
          params: {
            data: format(new Date(currentDate), 'yyyy-MM-dd'),
            emissora_id: emissora.value,
          },
        });
        if (response && response.data) {
          setChamadas(response.data);
        } else toast.error('Falha de comunicação com o sistema');

        setLoading(false);
      } catch (e) {
        setLoading(false);
        if (e instanceof Error) toast.error(e.message);
        else toast.error('Erro inesperado');
      }
    }
    loadChamadas();
    return () => {
      controller.abort();
    };
  }, [currentDate, emissora.value]);

  return (
    <Container>
      <nav>
        <Button size="lg" color="primary" onClick={() => navigate(-1)}>
          VOLTAR
          <FiArrowLeft />
        </Button>
        {dateFormatted && new Date(currentDate) && (
          <SelectDate
            dateFormatted={dateFormatted}
            currentDateZoned={String(new Date(currentDate))}
            handleNextDay={handleNextDay}
            handleDateChange={handleDateChange}
            handlePrevDay={handlePrevDay}
          />
        )}
        <div />
      </nav>
      <Card>
        <header>
          <h4>LISTA DE CHAMADAS</h4>
          <div>
            <Form onSubmit={() => {}}>
              <Input
                name="searchLocal"
                placeholder="Buscar na Tabela"
                icon={<FiSearch />}
                onChange={e => setFiltro(e.target.value)}
              />
            </Form>
          </div>
        </header>
        {loading ? (
          <Loader />
        ) : chamadasFiltradas && chamadasFiltradas.length ? (
          <Scroll>
            <Table>
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>MIDIA ID</th>
                  <th style={{ width: '70px' }}>CM</th>
                  <th>RETRANCA</th>
                  <th style={{ width: '150px' }}>STATUS</th>
                  <th style={{ width: '150px' }}>TIPO</th>
                  <th style={{ width: '65px' }}>AVAL.</th>
                  <th style={{ width: '65px' }}>OBS.</th>
                  <th style={{ width: '210px', textAlign: 'center' }}>
                    AVALIADO ÀS
                  </th>
                  <th style={{ width: '65px' }}>AÇÕES</th>
                </tr>
              </thead>

              <tbody>
                {!!chamadasFiltradas.length &&
                  chamadasFiltradas.map(ch => (
                    <tr key={ch.midia.id}>
                      <Td>{ch.midia.id}</Td>
                      <Td>{ch.codmaterial || '-'}</Td>
                      <Td>{ch.retranca}</Td>
                      <Td>
                        <Flag
                          name={ch.statusgeral.nome}
                          type={ch.statusgeral.type}
                        />
                      </Td>
                      <Td>
                        <Flag name={ch.tipo} type={ch.tipo} />
                      </Td>
                      <Td>
                        <Avatar
                          size="40"
                          name={ch.midia.avaliador.nome}
                          src={ch.midia.avaliador.avatar_url}
                          round
                        />
                      </Td>
                      <Td>
                        {ch.midia.obseng && (
                          <Button
                            fill
                            color="font"
                            round
                            onClick={() => handleRessalva(ch.midia.obseng)}
                          >
                            <FiMessageSquare />
                          </Button>
                        )}
                      </Td>
                      <td>
                        {format(
                          parseISO(ch.midia.checked_at),
                          "dd 'de' MMMM', às' H:mm'	",
                          {
                            locale: pt,
                          },
                        )}
                      </td>
                      <td>
                        <Align>
                          <Button
                            id={`id_reenviar_${ch.id}`}
                            fill
                            color="font"
                            round
                            onClick={() => handleReenviar(ch)}
                            disabled={
                              (profile.role !== Role.ADMIN &&
                                profile.role !== Role.ENG) ||
                              ch.statusgeral.nome === 'NA'
                            }
                          >
                            <FiRefreshCw />
                          </Button>
                          <Tooltip target={`id_reenviar_${ch.id}`}>
                            REENVIAR
                          </Tooltip>
                          <Button
                            id={`id_download_${ch.id}`}
                            fill
                            color="font"
                            round
                            onClick={() =>
                              handleDownload(
                                ch.midia.download_url,
                                ch.midia.filename,
                              )
                            }
                            disabled={loadingDownload}
                          >
                            <FiDownload />
                          </Button>
                          <Tooltip target={`id_download_${ch.id}`}>
                            DOWNLOAD
                          </Tooltip>
                        </Align>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Scroll>
        ) : (
          <NotFound img={NoSearch2} title="Chamadas">
            Nenhuma CHAMADA encontrada
          </NotFound>
        )}
      </Card>
    </Container>
  );
}
